// 对应 sidebar-07 的 NavMain：一级导航，带子项时可折叠；激活态按当前路由计算。
import { ChevronRight, type LucideIcon } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";

export interface NavItem {
  title: string;
  url: string;
  icon?: LucideIcon;
  /** 精确匹配（默认按前缀匹配）。 */
  end?: boolean;
  badge?: ReactNode;
  items?: { title: string; url: string; end?: boolean }[];
}

export function isActivePath(pathname: string, url: string, end?: boolean) {
  const p = pathname.replace(/\/$/, "") || "/";
  const u = url.replace(/\/$/, "") || "/";
  return end ? p === u : p === u || p.startsWith(`${u}/`);
}

function NavCollapsible({ childActive, children }: { childActive: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(childActive);
  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);
  return (
    <Collapsible asChild open={open} onOpenChange={setOpen} className="group/collapsible">
      {children}
    </Collapsible>
  );
}

export function NavMain({ label, items, className }: { label?: string; items: NavItem[]; className?: string }) {
  const { pathname } = useLocation();
  const { state, isMobile } = useSidebar();
  // 收起为图标时子菜单不可见：带子项的条目直接作为链接（指向 url）。
  const iconOnly = state === "collapsed" && !isMobile;
  return (
    <SidebarGroup className={className}>
      {label ? <SidebarGroupLabel className="font-display font-semibold tracking-[0.08em] uppercase text-muted-foreground">{label}</SidebarGroupLabel> : null}
      <SidebarMenu>
        {items.map((item) => {
          const childActive = !!item.items?.some((s) => isActivePath(pathname, s.url, s.end));
          if (!item.items?.length || iconOnly) {
            // 收起为图标时子项不可见，由父项代为显示激活态
            const active = isActivePath(pathname, item.url, item.end) || childActive;
            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild isActive={active} tooltip={item.title}>
                  <Link to={item.url}>
                    {item.icon ? <item.icon /> : null}
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
                {item.badge ? <SidebarMenuBadge>{item.badge}</SidebarMenuBadge> : null}
              </SidebarMenuItem>
            );
          }
          return (
            // 展开时只有子项显示激活态；进入子项页面时自动展开父项
            <NavCollapsible key={item.title} childActive={childActive}>
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton tooltip={item.title}>
                    {item.icon ? <item.icon /> : null}
                    <span>{item.title}</span>
                    <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items.map((sub) => (
                      <SidebarMenuSubItem key={sub.url}>
                        <SidebarMenuSubButton asChild isActive={isActivePath(pathname, sub.url, sub.end)}>
                          <Link to={sub.url}>
                            <span>{sub.title}</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </NavCollapsible>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
